import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComingSoonPage = () => {
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/coming-soon`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      } catch (error) {
        console.error('Failed to fetch coming soon settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!settings?.countdown_enabled || !settings?.countdown_date) return;

    const targetDate = new Date(settings.countdown_date).getTime();
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetDate - now;

      if (distance < 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setCountdown({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [settings?.countdown_enabled, settings?.countdown_date]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: 'Coming Soon',
          last_name: 'Signup',
          email: email,
          organization: '',
          country: '',
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("You're on the list! We'll notify you when we launch.");
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen bg-[#001428] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: settings.background_image 
          ? `url(${settings.background_image})` 
          : 'linear-gradient(135deg, #001428 0%, #00457f 50%, #001428 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black"
        style={{ opacity: settings.background_overlay_opacity || 0.7 }}
      />

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 py-12 max-w-2xl mx-auto">
        {/* Logo */}
        {settings.show_logo && (
          <div className="mb-8">
            <img 
              src="/assets/images/wnc-logo.png" 
              alt="World Nuclear Congress"
              className="h-20 md:h-24 mx-auto drop-shadow-2xl"
            />
          </div>
        )}

        {/* Headline */}
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
          {settings.headline || 'Coming Soon'}
        </h1>

        {/* Subheadline */}
        {settings.subheadline && (
          <p className="text-xl md:text-2xl text-yellow-400 font-semibold mb-6">
            {settings.subheadline}
          </p>
        )}

        {/* Description */}
        {(settings.description || !settings.subheadline) && (
          <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            {settings.description || 'The World Nuclear Congress is a premier international forum bringing together global leaders in nuclear energy, policy, and innovation. Sign up to be notified when registration opens.'}
          </p>
        )}

        {/* Countdown Timer */}
        {settings.countdown_enabled && settings.countdown_date && (
          <div className="flex justify-center gap-4 md:gap-6 mb-10">
            {[
              { label: 'Days', value: countdown.days },
              { label: 'Hours', value: countdown.hours },
              { label: 'Minutes', value: countdown.minutes },
              { label: 'Seconds', value: countdown.seconds }
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4 min-w-[60px] md:min-w-[80px]">
                  <span className="text-2xl md:text-4xl font-bold text-white">
                    {String(item.value).padStart(2, '0')}
                  </span>
                </div>
                <p className="text-xs md:text-sm text-gray-400 mt-2 uppercase tracking-wider">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        {settings.cta_enabled && (
          <div className="max-w-md mx-auto">
            {settings.cta_type === 'email' ? (
              submitted ? (
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                  <p className="text-green-400 font-semibold">
                    ✓ You're on the list! We'll notify you when we launch.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={settings.cta_email_placeholder || 'Enter your email'}
                    required
                    className="flex-1 px-5 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white font-bold rounded-full transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    style={{
                      boxShadow: '0 4px 20px rgba(245, 158, 11, 0.4)'
                    }}
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      settings.cta_text || 'Get Notified'
                    )}
                  </button>
                </form>
              )
            ) : settings.cta_type === 'link' && settings.cta_link ? (
              <a
                href={settings.cta_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-10 py-4 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white font-bold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
                style={{
                  boxShadow: '0 4px 20px rgba(245, 158, 11, 0.4)'
                }}
              >
                {settings.cta_text || 'Learn More'}
              </a>
            ) : null}
          </div>
        )}

        {/* Social Links */}
        {settings.show_social_links && settings.social_links && (
          <div className="flex justify-center gap-4 mt-10">
            {settings.social_links.twitter && (
              <a
                href={settings.social_links.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            )}
            {settings.social_links.linkedin && (
              <a
                href={settings.social_links.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            )}
            {settings.social_links.facebook && (
              <a
                href={settings.social_links.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-gray-500 text-sm">
          © {new Date().getFullYear()} World Nuclear Congress. All rights reserved.
        </p>
        <div className="mt-2 flex items-center justify-center gap-4 text-xs">
          {typeof window !== 'undefined' && localStorage.getItem('admin_token') ? (
            <>
              <a
                href={`${window.location.pathname === '/' ? '/' : window.location.pathname}?preview=1`}
                className="text-gray-400 hover:text-yellow-400 underline underline-offset-2"
                data-testid="view-full-site-link"
              >
                View full site (admin preview)
              </a>
              <span className="text-gray-600">·</span>
              <a
                href="/admin/homepage"
                className="text-gray-400 hover:text-yellow-400 underline underline-offset-2"
                data-testid="go-to-admin-link"
              >
                Admin dashboard
              </a>
            </>
          ) : (
            <a
              href="/admin/login"
              className="text-gray-400 hover:text-yellow-400 underline underline-offset-2"
              data-testid="admin-login-link"
            >
              Admin login
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComingSoonPage;
